'use client';
import {Box} from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import {styled} from '@mui/material/styles';
import dynamic from 'next/dynamic';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {NavArrow, SoundButton, Typography} from '../../primitives';
import {GalleryDots} from '../GalleryDots';
import {MediaType, type MediaAsset} from '../types';
import {useSwipeTrack} from '../useSwipeTrack';
import {HeroGallerySlide} from './HeroGallerySlide';

// The full-screen lightbox (FocusTrap + thumbnail strip + VideoPlayer) is only
// needed once the user opens it — code-split it so the detail-page bundle every
// visitor loads doesn't carry it. Mounted lazily below when `modalOpen` flips.
const GalleryModal = dynamic(() => import('../GalleryModal').then(m => m.GalleryModal), {ssr: false});

// Full-bleed cover (Figma 547:30948 — 1440×615), shorter on mobile. The arrows
// stay hidden until the frame is hovered or something inside it gains focus.
const Frame = styled(Box)(({theme}) => ({
  position: 'relative',
  width: '100%',
  height: 360,
  overflow: 'hidden',
  backgroundColor: theme.palette.colors.surface.neutral.subtle,
  touchAction: 'pan-y',
  [theme.breakpoints.up('md')]: {height: 615},
  '&:hover [data-hero-arrow], &:focus-within [data-hero-arrow]': {opacity: 1},
}));

// Covers the whole frame so a click (or tap) anywhere on the media opens the
// lightbox. The overlaid chrome (arrows, counter, mute) are siblings on top.
const MediaButton = styled(ButtonBase)({
  position: 'absolute',
  inset: 0,
  display: 'block',
});

const Track = styled(Box)({
  position: 'absolute',
  inset: 0,
  willChange: 'transform',
});

const Slide = styled(Box)({
  position: 'absolute',
  inset: 0,
});

// Bottom-right cluster (Figma: 30px inset, 12px gap) — mute toggle + counter.
const Cluster = styled(Box)(({theme}) => ({
  position: 'absolute',
  bottom: theme.spacing(3.75),
  insetInlineEnd: theme.spacing(3.75),
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

// Vertically-centered prev/next arrow, revealed on hover/focus (see Frame). A
// pointer affordance only — hidden below md, where the swipe gesture navigates.
const ArrowSlot = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 1,
  display: 'none',
  opacity: 0,
  transition: theme.transitions.create('opacity'),
  [theme.breakpoints.up('md')]: {display: 'block'},
}));

const Counter = styled(Box)(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 38,
  paddingInline: theme.spacing(3),
  borderRadius: theme.radii['2xl'],
  background: theme.palette.colors.surface.darkOverlayStrong,
}));

export interface HeroGalleryProps {
  assets: MediaAsset[];
  title?: string;
  onOpen?: () => void;
}

export function HeroGallery({assets, title, onOpen}: HeroGalleryProps) {
  const {t} = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const total = assets.length;
  const canPrev = activeIndex > 0;
  const canNext = activeIndex < total - 1;
  const swipe = useSwipeTrack({
    canPrev,
    canNext,
    enabled: total > 1,
    onPrev: () => {
      setActiveIndex(index => Math.max(index - 1, 0));
    },
    onNext: () => {
      setActiveIndex(index => Math.min(index + 1, total - 1));
    },
  });

  if (total === 0) {
    return null;
  }

  const hasMany = total > 1;
  const active = assets[activeIndex];
  const positions = [activeIndex - 1, activeIndex, activeIndex + 1].filter(index => index >= 0 && index < total);

  return (
    <Box component="section">
      <Frame {...swipe.handlers}>
        <MediaButton
          onClick={() => {
            if (swipe.consumeSwipe()) {
              return;
            }
            onOpen?.();
            setModalOpen(true);
          }}
        >
          <Track ref={swipe.trackRef}>
            {positions.map(index => (
              <Slide key={index} style={swipe.slideStyle(index - activeIndex)}>
                <HeroGallerySlide asset={assets[index]} muted={muted} paused={modalOpen || index !== activeIndex} />
              </Slide>
            ))}
          </Track>
        </MediaButton>

        {hasMany && (
          <>
            <ArrowSlot data-hero-arrow sx={{insetInlineStart: theme => theme.spacing(3.75)}}>
              <NavArrow
                direction="prev"
                disabled={!canPrev}
                onClick={() => {
                  swipe.go('prev');
                }}
              />
            </ArrowSlot>
            <ArrowSlot data-hero-arrow sx={{insetInlineEnd: theme => theme.spacing(3.75)}}>
              <NavArrow
                direction="next"
                disabled={!canNext}
                onClick={() => {
                  swipe.go('next');
                }}
              />
            </ArrowSlot>
            <GalleryDots total={total} activeIndex={activeIndex} />
          </>
        )}

        <Cluster>
          {active.type === MediaType.Video && (
            <SoundButton
              muted={muted}
              onClick={() => {
                setMuted(value => !value);
              }}
            />
          )}
          <Counter>
            <Typography
              size="body-2"
              weight="medium"
              as="span"
              sx={{
                letterSpacing: '-0.015em',
                color: theme => theme.palette.colors.text.neutral.inverse,
                textBox: 'trim-both cap alphabetic',
              }}
            >
              {t('n_gallery_count', {currentNumber: activeIndex + 1, totalNumber: total})}
            </Typography>
          </Counter>
        </Cluster>
      </Frame>

      {modalOpen && (
        <GalleryModal
          assets={assets}
          index={activeIndex}
          title={title}
          onIndexChange={setActiveIndex}
          onClose={() => {
            setModalOpen(false);
          }}
        />
      )}
    </Box>
  );
}

HeroGallery.displayName = 'HeroGallery';
